<template>
  <div>
    <b-input
      v-if="field.fieldDefinition.fieldType === 'string'"
      v-model="value"
      :label="label"
      hide-details></b-input>

    <b-input-number
      v-else-if="field.fieldDefinition.fieldType === 'integer'"
      v-model="value"
      :label="label"
      hide-details></b-input-number>

    <b-input-number
      v-else-if="field.fieldDefinition.fieldType === 'decimal'"
      v-model="value"
      :label="label"
      :step="0.01"
      hide-details></b-input-number>

    <b-textarea
      v-else-if="field.fieldDefinition.fieldType === 'text'"
      v-model="value"
      :label="label"
      rows="3"
      variant="outlined"
      density="compact"
      hide-details>
    </b-textarea>

    <div v-else-if="field.fieldDefinition.fieldType === 'boolean'">
      <b-label :label="label"></b-label>
      <b-checkbox v-model="value" hide-details></b-checkbox>
    </div>

    <b-input-date
      v-else-if="field.fieldDefinition.fieldType === 'date'"
      v-model="value"
      :label="label"
      hide-details></b-input-date>

    <!-- SELECT з entitySource — searchable (server-side пошук) -->
    <b-autocomplete
      v-else-if="
        field.fieldDefinition.fieldType === 'select' &&
        field.entitySource &&
        isSearchable(field.entitySource)
      "
      v-model="value"
      :items="searchItems"
      :label="label"
      :loading="searching"
      hide-details
      item-title="name"
      item-value="id"
      placeholder="Введіть мін. 3 символи"
      no-filter
      clearable
      variant="outlined"
      density="compact"
      @update:search="onSearch($event, field.entitySource)"></b-autocomplete>

    <!-- SELECT з entitySource — довідник -->
    <b-select
      v-else-if="
        field.fieldDefinition.fieldType === 'select' && field.entitySource
      "
      v-model="value"
      :label="label"
      :items="$directory.get(field.entitySource as any)"
      hide-details
      @focus="$directory.fill(field.entitySource as any)"></b-select>

    <!-- SELECT з ручними опціями -->
    <b-select
      v-else-if="
        field.fieldDefinition.fieldType === 'select' &&
        field.optionsJson?.length
      "
      v-model="value"
      :label="label"
      :items="field.optionsJson"
      hide-details></b-select>

    <!-- MULTISELECT з entitySource — searchable -->
    <b-autocomplete
      v-else-if="
        field.fieldDefinition.fieldType === 'multiselect' &&
        field.entitySource &&
        isSearchable(field.entitySource)
      "
      v-model="value"
      :items="searchItems"
      :label="label"
      :loading="searching"
      hide-details
      item-title="name"
      item-value="id"
      placeholder="Введіть мін. 3 символи"
      no-filter
      multiple
      chips
      clearable
      variant="outlined"
      density="compact"
      @update:search="onSearch($event, field.entitySource)"></b-autocomplete>

    <!-- MULTISELECT з entitySource — довідник -->
    <b-select
      v-else-if="
        field.fieldDefinition.fieldType === 'multiselect' && field.entitySource
      "
      v-model="value"
      :label="label"
      :items="$directory.get(field.entitySource as any)"
      multiple
      chips
      hide-details
      @focus="$directory.fill(field.entitySource as any)"></b-select>

    <!-- MULTISELECT з ручними опціями -->
    <b-select
      v-else-if="
        field.fieldDefinition.fieldType === 'multiselect' &&
        field.optionsJson?.length
      "
      v-model="value"
      :label="label"
      :items="field.optionsJson"
      item-title="name"
      item-value="id"
      multiple
      chips
      hide-details></b-select>
  </div>
</template>

<script setup lang="ts">
import {
  BInput,
  BInputNumber,
  BInputDate,
  BSelect,
  BCheckbox,
  BTextarea,
  BAutocomplete,
  BLabel,
} from 'best-modules/components'
import { FieldSnapshot } from '@/utils/types/documentTypeField'
import { computed, onMounted, ref } from 'vue'
import { useDocument } from '@/pages/document/single/document'
import { sendRequest } from 'best-modules/vue-utils'

defineOptions({
  name: 'FieldInput',
})

const props = defineProps<{
  field: FieldSnapshot
}>()

const { documentCredentials } = useDocument()

// --- value binding ---

const getFieldValue = (): any => {
  const fieldValue = documentCredentials.fieldValues.find(
    fv => fv.fieldDefinitionId === props.field.fieldDefinitionId
  )
  return fieldValue?.value ?? null
}

const setFieldValue = (value: any): void => {
  const fieldValue = documentCredentials.fieldValues.find(
    f => f.fieldDefinitionId === props.field.fieldDefinitionId
  )

  if (fieldValue) {
    Object.assign(fieldValue, { value })
  } else {
    documentCredentials.fieldValues.push({
      fieldDefinitionId: props.field.fieldDefinitionId,
      value,
    })
  }
}

const value = computed({
  get: () => getFieldValue(),
  set: val => setFieldValue(val),
})

const label = computed(
  () => props.field.label || props.field.fieldDefinition.name
)

// --- searchable entity sources ---

type SearchableSourceConfig = {
  searchUrl: string // /json/contractor?search={query}&paginate=false
  singleUrl: string // /json/contractor/single/{id}
}

const SEARCHABLE_SOURCES: Record<string, SearchableSourceConfig> = {
  contractors: {
    searchUrl: '/json/contractor?search={query}&paginate=false',
    singleUrl: '/json/contractor/single/{id}',
  },
}

const isSearchable = (source: string): boolean => source in SEARCHABLE_SOURCES

const searchItems = ref<{ id: number | string; name: string }[]>([])
const searching = ref(false)

let searchTimer: ReturnType<typeof setTimeout> | null = null

const fetchById = async (
  source: string,
  id: number | string
): Promise<void> => {
  const config = SEARCHABLE_SOURCES[source]
  if (!config) return
  try {
    const url = config.singleUrl.replace('{id}', String(id))
    const item = await sendRequest({
      name: `single_${source}_${id}`,
      method: 'get',
      url,
    })
    if (item && !searchItems.value.find(i => i.id === item.id)) {
      searchItems.value.push({ id: item.id, name: item.name })
    }
  } catch (e) {
    console.log(e)
  }
}

const onSearch = (query: string | null, source: string): void => {
  if (!query || query.length < 3) return

  if (searchTimer) clearTimeout(searchTimer)

  searchTimer = setTimeout(async () => {
    searching.value = true
    try {
      const config = SEARCHABLE_SOURCES[source]
      const url = config.searchUrl.replace('{query}', encodeURIComponent(query))
      const result = await sendRequest({
        name: `search_${source}`,
        method: 'get',
        url,
      })
      searchItems.value = Array.isArray(result) ? result : result?.data ?? []
    } finally {
      searching.value = false
    }
  }, 350)
}

onMounted(async () => {
  const source = props.field.entitySource
  if (!source) return

  if (isSearchable(source)) {
    // Searchable: preload selected item(s) by ID so the label shows correctly
    const currentValue = getFieldValue()
    if (!currentValue) return
    const ids = Array.isArray(currentValue) ? currentValue : [currentValue]
    await Promise.all(ids.map(id => fetchById(source, id)))
  } else {
    // Regular directory: fill eagerly if the field has a value
    if (getFieldValue() !== null) {
      // @ts-ignore
      window.$directory?.fill(source)
    }
  }
})
</script>
